import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Lock, Eye, EyeSlash, CheckCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const validate = () => {
    if (pw.length < 8) return "La password deve avere almeno 8 caratteri";
    if (pw !== pw2) return "Le password non corrispondono";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const err = validate();
    if (err) { setError(err); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password: pw });
      setDone(true);
      toast.success("Password aggiornata. Effettua il login con la nuova password.");
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } catch (e) {
      const d = e?.response?.data?.detail || "Token non valido o scaduto";
      setError(typeof d === "string" ? d : "Errore");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col md:flex-row md:h-screen min-h-screen" data-testid="reset-password-page">
      <div className="hidden md:block md:w-[55%] bg-gradient-to-br from-[#0D1F3C] via-[#142B5A] to-[#1B4FD8] relative">
        <div className="relative z-10 flex flex-col justify-between gap-10 md:h-full p-8 sm:p-12">
          <div className="text-white text-2xl font-bold tracking-tight">SELLY®</div>
          <div className="text-white">
            <h1 className="text-4xl sm:text-5xl font-bold leading-tight">Reimposta password</h1>
            <p className="mt-4 text-white/80 max-w-md">Inserisci una nuova password sicura per accedere nuovamente al tuo account.</p>
          </div>
          <div className="text-white/50 text-xs">© 2026 SELLY® — HR SaaS Platform</div>
        </div>
      </div>
      <div className="w-full md:w-[45%] flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-md">
          {done ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <CheckCircle size={64} weight="fill" className="text-emerald-500 mx-auto" />
              <h2 className="text-2xl font-bold text-[#0D1F3C]">Password aggiornata</h2>
              <p className="text-sm text-[#0D1F3C]/70">Verrai reindirizzato al login...</p>
              <Link to="/login" className="text-[#1B4FD8] hover:underline text-sm">Vai al login</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <h2 className="text-3xl font-bold text-[#0D1F3C]">Nuova password</h2>
                <p className="text-sm text-[#0D1F3C]/60 mt-1">Scegli una password con almeno 8 caratteri.</p>
              </div>
              <div>
                <Label htmlFor="pw" className="text-xs font-medium text-[#0D1F3C]/70">Nuova password</Label>
                <div className="relative mt-1">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D1F3C]/40" />
                  <Input id="pw" type={showPw ? "text" : "password"} value={pw}
                    onChange={(e) => setPw(e.target.value)} className="pl-9 pr-9"
                    autoFocus required minLength={8} data-testid="reset-pw-input" />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0D1F3C]/40 hover:text-[#0D1F3C]"
                    aria-label="Mostra/nascondi password">
                    {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="pw2" className="text-xs font-medium text-[#0D1F3C]/70">Conferma password</Label>
                <div className="relative mt-1">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D1F3C]/40" />
                  <Input id="pw2" type={showPw ? "text" : "password"} value={pw2}
                    onChange={(e) => setPw2(e.target.value)} className="pl-9"
                    required minLength={8} data-testid="reset-pw2-input" />
                </div>
              </div>
              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2" data-testid="reset-error">{error}</div>}
              <Button type="submit" disabled={busy} className="w-full bg-[#1B4FD8] hover:bg-[#0D1F3C]" data-testid="reset-submit-btn">
                {busy ? "Aggiornamento..." : "Imposta nuova password"}
              </Button>
              <div className="text-center text-xs text-[#0D1F3C]/60">
                <Link to="/login" className="text-[#1B4FD8] hover:underline">Torna al login</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
